import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";

const DispatchPlancurrent = () => {
    const [loading, setLoading] = useState(false);
    const [dispatchData, setDispatchData] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const perPage = 10;

    // =========================
    // FETCH DISPATCH PLAN
    // =========================
    const fetchDispatchPlan = async () => {
        const token = getAuthToken();

        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        try {
            setLoading(true);

            const response = await axios.post(
                `${config.API}/senmanager/dispatchplan`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success) {
                setDispatchData(
                    response.data?.data || []
                );
                setCurrentPage(1);
            } else {
                setDispatchData([]);

                toast.warning(
                    response.data?.message ||
                    "No dispatch plan found."
                );
            }
        } catch (error) {
            console.error(
                "Dispatch Plan Error:",
                error
            );

            setDispatchData([]);

            toast.error(
                error.response?.data?.message ||
                "Failed to load dispatch plan."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDispatchPlan();
    }, []);

    // =========================
    // SEARCH
    // =========================
    const filteredData = useMemo(() => {
        const value = search.trim().toLowerCase();

        if (!value) {
            return dispatchData;
        }

        return dispatchData.filter((item) => {
            return [
                item.poNumber,
                item.productCode,
                item.productName,
                item.railway,
                item.entryType,
                item.poType,
                item.unitName,
                item.deliveryMonthName,
            ].some((field) =>
                String(field || "")
                    .toLowerCase()
                    .includes(value)
            );
        });
    }, [dispatchData, search]);

    // Reset page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    // =========================
    // PAGINATION
    // =========================
    const totalPages = Math.ceil(
        filteredData.length / perPage
    );

    const paginatedData = useMemo(() => {
        const start =
            (currentPage - 1) * perPage;

        return filteredData.slice(
            start,
            start + perPage
        );
    }, [filteredData, currentPage]);

    // =========================
    // SUMMARY
    // =========================
    const summary = useMemo(() => {
        const uniquePO = new Set(
            filteredData
                .map((item) => item.poNumber)
                .filter(Boolean)
        ).size;

        const pendingQty = filteredData.reduce(
            (sum, item) =>
                sum +
                Number(item.totalPendingQty || 0),
            0
        );

        const products = new Set(
            filteredData
                .map((item) => item.productCode)
                .filter(Boolean)
        ).size;

        const railways = new Set(
            filteredData
                .map((item) => item.railway)
                .filter(Boolean)
        ).size;

        return {
            uniquePO,
            pendingQty,
            products,
            railways,
        };
    }, [filteredData]);

    // =========================
    // FORMAT NUMBER
    // =========================
    const formatNumber = (value) => {
        return Number(value || 0).toLocaleString(
            "en-IN"
        );
    };

    // =========================
    // FORMAT DATE
    // =========================
    const formatDate = (value) => {
        if (!value) {
            return "-";
        }

        const date = new Date(value);

        if (isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    return (
        <div className="container-fluid py-4">

            {/* ================= HEADER ================= */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">

                <div>
                    <div className="d-flex align-items-center gap-2">

                        <h4 className="fw-bold mb-0">
                            Dispatch Plan
                        </h4>

                        <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle">
                            ERP
                        </span>

                    </div>

                    <div className="text-muted small mt-1">
                        Planned deliveries and pending
                        quantities
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                    onClick={fetchDispatchPlan}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                            />
                            Loading...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-arrow-clockwise" />
                            Refresh
                        </>
                    )}
                </button>

            </div>

            {/* ================= SUMMARY CARDS ================= */}
            <div className="row g-3 mb-4">

                {/* PO */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Unique PO
                                    </div>

                                    <div className="fs-4 fw-bold">
                                        {formatNumber(
                                            summary.uniquePO
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-primary-subtle text-primary p-2">
                                    <i className="bi bi-file-earmark-text fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

                {/* PRODUCTS */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Products
                                    </div>

                                    <div className="fs-4 fw-bold">
                                        {formatNumber(
                                            summary.products
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-info-subtle text-info p-2">
                                    <i className="bi bi-box-seam fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

                {/* PENDING */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Pending Quantity
                                    </div>

                                    <div className="fs-4 fw-bold text-warning">
                                        {formatNumber(
                                            summary.pendingQty
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-warning-subtle text-warning p-2">
                                    <i className="bi bi-hourglass-split fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

                {/* RAILWAYS */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100">

                        <div className="card-body p-3">

                            <div className="d-flex justify-content-between">

                                <div>
                                    <div className="text-muted small mb-1">
                                        Railways
                                    </div>

                                    <div className="fs-4 fw-bold">
                                        {formatNumber(
                                            summary.railways
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-3 bg-success-subtle text-success p-2">
                                    <i className="bi bi-train-front fs-5" />
                                </div>

                            </div>

                        </div>

                    </div>
                </div>

            </div>

            {/* ================= MAIN TABLE ================= */}
            <div className="card border-0 shadow-sm">

                {/* TOOLBAR */}
                <div className="card-header bg-white border-0 p-3 p-lg-4">

                    <div className="row align-items-center g-3">

                        <div className="col-12 col-lg-5">

                            <h6 className="fw-bold mb-1">
                                Dispatch Plan
                            </h6>

                            <small className="text-muted">
                                {filteredData.length} records
                            </small>

                        </div>

                        <div className="col-12 col-lg-7">

                            <div className="d-flex justify-content-lg-end">

                                <div
                                    className="input-group"
                                    style={{
                                        maxWidth: "420px",
                                    }}
                                >

                                    <span className="input-group-text bg-white">
                                        <i className="bi bi-search" />
                                    </span>

                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search PO, product, railway..."
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(
                                                e.target.value
                                            )
                                        }
                                    />

                                    {search && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() =>
                                                setSearch("")
                                            }
                                        >
                                            <i className="bi bi-x" />
                                        </button>
                                    )}

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

                {/* TABLE */}
                <div className="card-body p-0">

                    {loading ? (

                        <div className="text-center py-5">

                            <div
                                className="spinner-border text-primary"
                                role="status"
                            />

                            <div className="text-muted mt-3">
                                Loading dispatch plan...
                            </div>

                        </div>

                    ) : (

                        <div className="table-responsive">

                            <table className="table table-hover align-middle mb-0">

                                <thead className="table-light">

                                    <tr>

                                        <th className="px-4">
                                            #
                                        </th>

                                        <th>
                                            PO NUMBER
                                        </th>

                                        <th>
                                            PO DATE
                                        </th>

                                        <th>
                                            RAILWAY
                                        </th>

                                        <th>
                                            PRODUCT
                                        </th>

                                        <th>
                                            UNIT
                                        </th>

                                        <th>
                                            DELIVERY MONTH
                                        </th>

                                        <th>
                                            INSPECTION
                                        </th>

                                        <th>
                                            PRODUCTION
                                        </th>

                                        <th className="text-end">
                                            PENDING QTY
                                        </th>

                                        <th>
                                            ACTUAL DP
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {paginatedData.length > 0 ? (

                                        paginatedData.map(
                                            (item, index) => (
                                                <tr
                                                    key={
                                                        item.erpItemId ||
                                                        index
                                                    }
                                                >

                                                    {/* NUMBER */}
                                                    <td className="px-4 text-muted fw-semibold">
                                                        {(currentPage -
                                                            1) *
                                                            perPage +
                                                            index +
                                                            1}
                                                    </td>

                                                    {/* PO */}
                                                    <td>

                                                        <div className="fw-semibold">
                                                            {item.poNumber ||
                                                                "-"}
                                                        </div>

                                                        <small className="text-muted">
                                                            ERP PO:{" "}
                                                            {item.erpPoId ||
                                                                "-"}
                                                        </small>

                                                    </td>

                                                    {/* PO DATE */}
                                                    <td className="text-nowrap">
                                                        {formatDate(
                                                            item.poDate
                                                        )}
                                                    </td>

                                                    {/* RAILWAY */}
                                                    <td>

                                                        <span className="badge bg-light text-dark border">
                                                            {item.railway ||
                                                                "-"}
                                                        </span>

                                                    </td>

                                                    {/* PRODUCT */}
                                                    <td
                                                        style={{
                                                            minWidth:
                                                                "320px",
                                                            maxWidth:
                                                                "460px",
                                                        }}
                                                    >

                                                        <div className="fw-semibold">
                                                            {item.productName ||
                                                                "-"}
                                                        </div>

                                                        <small className="text-muted">
                                                            {item.productCode ||
                                                                "-"}
                                                        </small>

                                                    </td>

                                                    {/* UNIT */}
                                                    <td>
                                                        {item.unitName ||
                                                            "-"}
                                                    </td>

                                                    {/* DELIVERY MONTH */}
                                                    <td>

                                                        <span className="badge bg-primary-subtle text-primary">
                                                            {item.deliveryMonthName ||
                                                                "-"}
                                                        </span>

                                                    </td>

                                                    {/* INSPECTION */}
                                                    <td className="text-nowrap">

                                                        <div className="small fw-semibold">
                                                            {item.inspectionToCommence ||
                                                                "-"}
                                                        </div>

                                                    </td>

                                                    {/* PRODUCTION */}
                                                    <td className="text-nowrap">

                                                        <div className="small fw-semibold">
                                                            {item.productionToStart ||
                                                                "-"}
                                                        </div>

                                                    </td>

                                                    {/* PENDING */}
                                                    <td className="text-end">

                                                        <span className="fw-bold text-warning">
                                                            {formatNumber(
                                                                item.totalPendingQty
                                                            )}
                                                        </span>

                                                    </td>

                                                    {/* ACTUAL DP */}
                                                    <td>

                                                        {item.actualDP ? (
                                                            <span className="badge bg-success-subtle text-success">
                                                                {
                                                                    item.actualDP
                                                                }
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">
                                                                Not Set
                                                            </span>
                                                        )}

                                                    </td>

                                                </tr>
                                            )
                                        )

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan="11"
                                                className="text-center py-5"
                                            >

                                                <div className="text-muted mb-2">
                                                    <i className="bi bi-calendar-x fs-2" />
                                                </div>

                                                <div className="fw-semibold">
                                                    No dispatch plan
                                                    found
                                                </div>

                                                <small className="text-muted">
                                                    Try a different
                                                    search term.
                                                </small>

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                                {/* TOTAL */}
                                {filteredData.length > 0 && (
                                    <tfoot className="table-light">

                                        <tr>

                                            <th
                                                colSpan="9"
                                                className="px-4 text-end"
                                            >
                                                TOTAL PENDING
                                            </th>

                                            <th className="text-end text-warning">
                                                {formatNumber(
                                                    summary.pendingQty
                                                )}
                                            </th>

                                            <th />

                                        </tr>

                                    </tfoot>
                                )}

                            </table>

                        </div>

                    )}

                </div>

                {/* ================= PAGINATION ================= */}
                {!loading &&
                    filteredData.length > 0 &&
                    totalPages > 1 && (
                        <div className="border-top px-3 py-3">

                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">

                                {/* RECORD INFO */}
                                <div className="text-muted small text-nowrap">

                                    Showing{" "}

                                    <strong>
                                        {(currentPage -
                                            1) *
                                            perPage +
                                            1}
                                    </strong>

                                    {" - "}

                                    <strong>
                                        {Math.min(
                                            currentPage *
                                                perPage,
                                            filteredData.length
                                        )}
                                    </strong>

                                    {" of "}

                                    <strong>
                                        {filteredData.length}
                                    </strong>

                                </div>

                                {/* PAGINATION */}
                                <nav>
                                    <ul className="pagination pagination-sm mb-0 flex-nowrap">

                                        {/* PREVIOUS */}
                                        <li
                                            className={`page-item ${
                                                currentPage ===
                                                1
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                disabled={
                                                    currentPage ===
                                                    1
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        (
                                                            page
                                                        ) =>
                                                            page -
                                                            1
                                                    )
                                                }
                                            >

                                                <i className="bi bi-chevron-left" />

                                                <span className="d-none d-md-inline ms-1">
                                                    Previous
                                                </span>

                                            </button>

                                        </li>

                                        {/* FIRST PAGE */}
                                        <li
                                            className={`page-item ${
                                                currentPage ===
                                                1
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                onClick={() =>
                                                    setCurrentPage(
                                                        1
                                                    )
                                                }
                                            >
                                                1
                                            </button>

                                        </li>

                                        {/* LEFT ELLIPSIS */}
                                        {currentPage >
                                            3 && (
                                            <li className="page-item disabled">

                                                <span className="page-link">
                                                    ...
                                                </span>

                                            </li>
                                        )}

                                        {/* MIDDLE PAGES */}
                                        {Array.from(
                                            {
                                                length: 3,
                                            },
                                            (
                                                _,
                                                index
                                            ) =>
                                                currentPage -
                                                1 +
                                                index
                                        )
                                            .filter(
                                                (
                                                    page
                                                ) =>
                                                    page >
                                                        1 &&
                                                    page <
                                                        totalPages
                                            )
                                            .map(
                                                (
                                                    page
                                                ) => (
                                                    <li
                                                        key={
                                                            page
                                                        }
                                                        className={`page-item ${
                                                            currentPage ===
                                                            page
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                    >

                                                        <button
                                                            type="button"
                                                            className="page-link"
                                                            onClick={() =>
                                                                setCurrentPage(
                                                                    page
                                                                )
                                                            }
                                                        >
                                                            {
                                                                page
                                                            }
                                                        </button>

                                                    </li>
                                                )
                                            )}

                                        {/* RIGHT ELLIPSIS */}
                                        {currentPage <
                                            totalPages -
                                                2 && (
                                            <li className="page-item disabled">

                                                <span className="page-link">
                                                    ...
                                                </span>

                                            </li>
                                        )}

                                        {/* LAST PAGE */}
                                        {totalPages >
                                            1 && (
                                            <li
                                                className={`page-item ${
                                                    currentPage ===
                                                    totalPages
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >

                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    onClick={() =>
                                                        setCurrentPage(
                                                            totalPages
                                                        )
                                                    }
                                                >
                                                    {
                                                        totalPages
                                                    }
                                                </button>

                                            </li>
                                        )}

                                        {/* NEXT */}
                                        <li
                                            className={`page-item ${
                                                currentPage ===
                                                totalPages
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                        >

                                            <button
                                                type="button"
                                                className="page-link"
                                                disabled={
                                                    currentPage ===
                                                    totalPages
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        (
                                                            page
                                                        ) =>
                                                            page +
                                                            1
                                                    )
                                                }
                                            >

                                                <span className="d-none d-md-inline me-1">
                                                    Next
                                                </span>

                                                <i className="bi bi-chevron-right" />

                                            </button>

                                        </li>

                                    </ul>
                                </nav>

                            </div>

                        </div>
                    )}

            </div>
        </div>
    );
};

export default DispatchPlancurrent;